// The row a foreign-key value names, as the lookup returns it (GET .../data/tables/{table}/lookup,
// backend operate/browse.py): its columns, masked columns as the server masked
// them (***). Used by the foreign-key field's options and card (ForeignKeyWidget.jsx) and by the
// preview of a foreign-key cell in the table view (DataTablePage.jsx).
//
//   <RowDetails columns={lookup.columns} row={option.row} />
//   const ref = useReferencedRow(projectId, fk, key)   // {loading, row, columns, error}; row null = no such row
import { useEffect, useState } from "react";
import { api } from "../../api";
import { cellText, lookupPath } from "./rowFormUtils";

const MONO = "'JetBrains Mono', monospace";

export function RowDetails({ columns, row }) {
  const cols = columns && columns.length ? columns : Object.keys(row || {}).map(name => ({ name }));
  return (
    <dl style={{ display: "grid", gridTemplateColumns: "minmax(0, max-content) minmax(0, 1fr)", gap: "2px 12px",
                 margin: 0, fontSize: 12 }}>
      {cols.map(c => (
        <div key={c.name} style={{ display: "contents" }}>
          <dt style={{ fontFamily: MONO, color: "#4b5563", overflowWrap: "anywhere" }}>{c.name}</dt>
          <dd style={{ margin: 0, color: "#1a1a1a", overflowWrap: "anywhere" }}>
            {cellText(c, row ? row[c.name] : null)}
            {c.masked && <span style={{ color: "#991b1b" }}> (personal data, masked)</span>}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** The referenced row whose key is `value` (null/undefined/"" -> nothing is fetched). */
export function useReferencedRow(projectId, fk, value, { known = null, delayMs = 250 } = {}) {
  const [state, setState] = useState({ loading: false, row: null, columns: [], error: null, key: null });
  const empty = value === null || value === undefined || value === "";
  const knownKey = known ? String(known.key) : null;

  useEffect(() => {
    if (empty || !fk || !fk.table) {
      setState({ loading: false, row: null, columns: [], error: null, key: null });
      return undefined;
    }
    if (known && knownKey === String(value)) {        // the option just picked: nothing to fetch
      setState({ loading: false, row: known.row, columns: known.columns, error: null, key: String(value) });
      return undefined;
    }
    let live = true;
    setState(s => ({ ...s, loading: true }));
    const timer = setTimeout(() => {
      api.get(lookupPath(projectId, fk, { key: value, limit: 1 }))
        .then(r => {
          if (!live) return;
          const hit = (r.options || [])[0];
          setState({ loading: false, row: hit ? hit.row : null, columns: r.columns || [], error: null,
                     key: String(value) });
        })
        .catch(err => { if (live) setState({ loading: false, row: null, columns: [], error: err, key: String(value) }); });
    }, delayMs);
    return () => { live = false; clearTimeout(timer); };
    // `known` is read through knownKey: a new object for the same pick must not fetch again
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, fk && fk.table, fk && fk.column, value, empty, knownKey, delayMs]);

  return state;
}
